import React, { createContext, useContext, useState, useEffect } from 'react';

import api from '../utils/api';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const [selectedCouponCode, setSelectedCouponCode] = useState(() => {
    try { return sessionStorage.getItem('akf_coupon') || ''; } catch { return ''; }
  });
  const [validatedCoupon, setValidatedCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [items, setItems] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem('akf_cart')) || [];
      return raw.filter(i => i && i.productId != null && i.weight).map(i => ({
        ...i,
        price: Number(i.price) || 0,
        mrp: Number(i.mrp) || Number(i.price) || 0,
        quantity: Number(i.quantity) || 1,
      }));
    } catch { return []; }
  });

  useEffect(() => {
    localStorage.setItem('akf_cart', JSON.stringify(items));
  }, [items]);

  const getKey = (productId, weight, bundleId = '') => `${productId}_${weight}_${bundleId || 'regular'}`;

  const addItem = (product, variant, quantity = 1) => {
    const pid = product._id ?? product.id;
    if (pid == null) {
      console.error('addItem: product is missing both _id and id', product);
      return;
    }
    setItems(prev => {
      const key = getKey(pid, variant.weight);
      const existing = prev.find(i => getKey(i.productId, i.weight, i.bundleId) === key);
      if (existing) {
        return prev.map(i => getKey(i.productId, i.weight, i.bundleId) === key
          ? { ...i, quantity: i.quantity + quantity }
          : i
        );
      }
      return [...prev, {
        productId: pid,
        name: product.name,
        thumbnail: product.thumbnail,
        slug: product.slug,
        weight: variant.weight,
        price: Number(variant.price) || 0,
        mrp: Number(variant.mrp) || Number(variant.price) || 0,
        quantity,
        isVeg: product.isVeg
      }];
    });
  };

  const addHamper = (selections, customization = {}) => {
    const bundleId = `hamper_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    const details = {
      personalMessage: String(customization.personalMessage || '').trim(),
      styleInstructions: String(customization.styleInstructions || '').trim(),
    };
    const hamperItems = selections.map(({ product, variant }) => ({
      productId: product._id ?? product.id,
      name: product.name,
      thumbnail: product.thumbnail,
      slug: product.slug,
      weight: variant.weight,
      price: Number(variant.price) || 0,
      mrp: Number(variant.mrp) || Number(variant.price) || 0,
      quantity: 1,
      isVeg: product.isVeg,
      bundleId,
      bundleType: 'hamper',
      bundleLabel: 'Custom Gift Hamper',
      customization: details,
    })).filter(item => item.productId != null);
    setItems(prev => [...prev, ...hamperItems]);
    return bundleId;
  };

  // Adds a combo as one bundle: its members share a bundleId and carry the
  // comboId so the server can re-derive the bundle price at checkout. Prices
  // stored here are the catalogue prices scaled to the combo total, purely so
  // the cart displays the right figure — the server never trusts them.
  const addCombo = (combo, selections) => {
    const bundleId = `combo_${combo._id ?? combo.id}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    const catalogueTotal = selections.reduce((sum, s) => sum + (Number(s.variant.price) || 0), 0);
    const comboPrice = Number(combo.price) || 0;

    let allocated = 0;
    const comboItems = selections.map(({ product, variant }, index) => {
      const isLast = index === selections.length - 1;
      const share = isLast || catalogueTotal <= 0
        ? comboPrice - allocated
        : Math.round((Number(variant.price) || 0) / catalogueTotal * comboPrice * 100) / 100;
      allocated += share;
      return {
        productId: product._id ?? product.id,
        name: product.name,
        thumbnail: product.thumbnail,
        slug: product.slug,
        weight: variant.weight,
        price: Number(share.toFixed(2)),
        mrp: Number(variant.price) || 0,       // strikethrough = what it costs loose
        quantity: 1,
        isVeg: product.isVeg,
        bundleId,
        bundleType: 'combo',
        bundleLabel: combo.name,
        comboId: combo._id ?? combo.id,
      };
    }).filter(item => item.productId != null);

    setItems(prev => [...prev, ...comboItems]);
    return bundleId;
  };

  const updateQuantity = (productId, weight, quantity, bundleId = '') => {
    if (quantity < 1) return removeItem(productId, weight, bundleId);
    const key = getKey(productId, weight, bundleId);
    setItems(prev => prev.map(i => getKey(i.productId, i.weight, i.bundleId) === key ? { ...i, quantity } : i));
  };

  const removeItem = (productId, weight, bundleId = '') => {
    const key = getKey(productId, weight, bundleId);
    setItems(prev => prev.filter(i => getKey(i.productId, i.weight, i.bundleId) !== key));
  };

  const removeBundle = bundleId => setItems(prev => prev.filter(i => i.bundleId !== bundleId));
  const clearCart = () => { setItems([]); setSelectedCouponCode(''); setValidatedCoupon(null); };

  const subtotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);
  const savings = items.reduce((sum, i) => sum + (i.mrp - i.price) * i.quantity, 0);

  const couponUserId = user?._id || user?.id;
  const appliedCoupon = validatedCoupon?.code === selectedCouponCode && validatedCoupon?.subtotal === subtotal && validatedCoupon?.userId === couponUserId ? validatedCoupon : null;
  useEffect(() => {
    try {
      if (selectedCouponCode) sessionStorage.setItem('akf_coupon', selectedCouponCode);
      else sessionStorage.removeItem('akf_coupon');
    } catch {}
  }, [selectedCouponCode]);
  useEffect(() => {
    let cancelled = false;
    setValidatedCoupon(null);
    if (!selectedCouponCode || !items.length) {
      setCouponLoading(false);
      if (!items.length) setSelectedCouponCode('');
      return;
    }
    setCouponLoading(true);
    api.post('/coupons/validate', { code: selectedCouponCode, subtotal, userId: couponUserId })
      .then(({ data }) => {
        if (cancelled) return;
        if (!data.success) throw new Error(data.message || 'Invalid coupon');
        setValidatedCoupon({ code: selectedCouponCode, discount: Number(data.discount) || 0, message: data.message, subtotal, userId: couponUserId });
      })
      .catch(error => {
        if (cancelled) return;
        setSelectedCouponCode('');
        toast.error(error.response?.data?.message || error.message || 'Could not validate coupon');
      })
      .finally(() => { if (!cancelled) setCouponLoading(false); });
    return () => { cancelled = true; };
  }, [selectedCouponCode, subtotal, couponUserId, items.length]);

  return (
    <CartContext.Provider value={{
      items, addItem, addHamper, addCombo, updateQuantity, removeItem, removeBundle, clearCart,
      subtotal, totalItems, savings, appliedCoupon, couponLoading: couponLoading || Boolean(selectedCouponCode && !appliedCoupon), setSelectedCouponCode
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
